// src/features/Taxonomy/DragLayer/FeedbackDragLayer.tsx

import { DragOutlined } from '@ant-design/icons';
import { useEffect, useMemo,useState } from 'react';
import { useDragLayer } from 'react-dnd';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import Transition from '@/components/Common/Transition';
import Portal from '@/components/Portal/Portal';
import Typo from '@/components/Typography/Typo';
import { animation } from '@/styles/style';

import type { FeedbackCardDragItem } from '../../FeedbackCard/FeedbackCard';

const FeedbackDragLayer = () => {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  const [tempItem, setTempItem] = useState<FeedbackCardDragItem | null>(null);

  useEffect(() => {
    if (item) {
      setTempItem(item);
    } else {
      setTimeout(() => {
        setTempItem(null);
      }, 300);
    }
  }, [item]);

  const dragPreview = useMemo(() => {
    if (!tempItem) return null;

    return tempItem.isMultiple ? (
      <>
        <MainPreview>
          <DragOutlined style={{ fontSize: 20 }} />
          <Typo>Move {tempItem.feedbacks.length} feedback</Typo>
        </MainPreview>
        <StackedPreviews>
          {[...Array(Math.min(5, tempItem.feedbacks.length - 1))].map(
            (_, index) => (
              <PreviewStack
                key={index}
                style={{
                  transform: `translate(${(index + 1) * 4}px, ${
                    (index + 1) * 4
                  }px)`,
                  zIndex: -index,
                }}
              />
            ),
          )}
        </StackedPreviews>
      </>
    ) : (
      <MainPreview>
        <DragOutlined style={{ fontSize: 20 }} />
        <Typo>Move 1 feedback</Typo>
      </MainPreview>
    );
  }, [tempItem]);

  return (
    <Portal id="preview-layer">
      <Transition status={isDragging && !!currentOffset && !!tempItem}>
        <DragLayerStyle
          style={{
            transform: currentOffset
              ? `translate3d(${currentOffset.x}px, ${currentOffset.y}px, 0)`
              : 'translate3d(0px, 0px, 0)',
          }}
        >
          <DragPreviewContainer>{dragPreview}</DragPreviewContainer>
        </DragLayerStyle>
      </Transition>
    </Portal>
  );
};

const DragLayerStyle = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 300px;
  will-change: transform;
  transform-style: preserve-3d;
  backface-visibility: hidden;

  animation: ${animation.fadeIn} 0.3s;
`;

const DragPreviewContainer = styled.div`
  position: relative;
`;

const MainPreview = styled(Flex)`
  position: relative;
  z-index: 10;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  height: 40px;
  background-color: ${(props) => props.theme.colors.primary100};
  color: ${(props) => props.theme.colors.FL100};
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  font-weight: 500;
  font-size: 14px;
`;

const StackedPreviews = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;

const PreviewStack = styled.div`
  position: absolute;
  width: 300px;
  height: 40px;
  color: ${(props) => props.theme.colors.FL100};
  background-color: ${(props) => props.theme.colors.primary100};
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
`;

export default FeedbackDragLayer;
